// Minimal reCAPTCHA v3 loader/executor.
//
// We keep this dependency-free (no 3rd-party packages) and load the Google
// script on demand.

declare global {
  interface Window {
    grecaptcha?: {
      // These are present when the Google script fully initializes.
      ready?: (cb: () => void) => void;
      execute?: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const RECAPTCHA_SCRIPT_ID = 'recaptcha-v3-script';

let scriptLoadPromise: Promise<void> | null = null;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const getSiteKey = (): string | null => {
  const key = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  return key && key.trim().length > 0 ? key.trim() : null;
};

const waitForGrecaptcha = async (timeoutMs: number): Promise<void> => {
  const start = Date.now();
  // Wait until grecaptcha is actually ready (some browsers/extensions can delay init).
  while (Date.now() - start < timeoutMs) {
    if (window.grecaptcha?.ready && window.grecaptcha?.execute) return;
    await sleep(50);
  }
  throw new Error('reCAPTCHA failed to initialize. Please refresh the page and try again.');
};

const ensureScriptLoaded = async (siteKey: string): Promise<void> => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('reCAPTCHA can only be used in the browser.');
  }

  if (window.grecaptcha?.execute) return;
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA script.')));
      // If the script tag exists but was already loaded, resolve immediately.
      if (window.grecaptcha?.execute) resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script.'));
    document.head.appendChild(script);
  });

  // If the script fails to load, allow future calls to retry.
  scriptLoadPromise = scriptLoadPromise.catch((err) => {
    scriptLoadPromise = null;
    throw err;
  });

  return scriptLoadPromise;
};

export const warmupRecaptchaV3 = async (): Promise<void> => {
  const siteKey = getSiteKey();
  if (!siteKey) return;
  await ensureScriptLoaded(siteKey);
  await waitForGrecaptcha(3000);
};

/**
 * Execute reCAPTCHA v3 for a given action and return the token.
 *
 * Returns `null` when not configured (no `REACT_APP_RECAPTCHA_SITE_KEY`) or in
 * tests.
 */
export const maybeGetRecaptchaToken = async (action: string): Promise<string | null> => {
  if (process.env.NODE_ENV === 'test') return null;

  const siteKey = getSiteKey();
  if (!siteKey) return null;

  await ensureScriptLoaded(siteKey);
  await waitForGrecaptcha(3000);

  const grecaptcha = window.grecaptcha;
  if (!grecaptcha?.ready || !grecaptcha.execute) {
    throw new Error('reCAPTCHA failed to initialize. Please refresh the page and try again.');
  }

  const token = await Promise.race<string>([
    new Promise<string>((resolve, reject) => {
      grecaptcha.ready!(() => {
        grecaptcha
          .execute!(siteKey, { action })
          .then(resolve)
          .catch((err) => reject(err instanceof Error ? err : new Error('reCAPTCHA execution failed.')));
      });
    }),
    (async () => {
      await sleep(8000);
      throw new Error('reCAPTCHA timed out. Please try again.');
    })(),
  ]);

  if (!token || token.trim().length < 10) {
    throw new Error('reCAPTCHA failed. Please try again.');
  }

  return token;
};
